<template>
    <!-- Invoice page (also opened in the mobile app web view: ?is_from_mobile=1 hides Print), app theme like the
         RQ System Summary: purple company band, invoice / bill-to blocks, line items, totals, approval + signature -->
    <div v-if="!apiCallback" class="w-full max-w-[960px] mx-auto px-[10px] tablet:px-0 font-roboto print:max-w-full print:px-0"
        :class="(is_from_mobile == 0 && !$helper.isProxyLoggedIn()) ? 'mt-0' : 'mt-[20px]'">
        <!-- top bar -->
        <div class="flex items-center justify-between gap-[10px] mb-[15px] print:hidden">
            <button type="button"
                class="inline-flex items-center gap-2 px-[14px] py-[6px] rounded-full border-0 text-[14px] text-[#8479E1] bg-[#fff] shadow-[0_0_10px_0_#0000001A] hover:bg-[#F3F0FF] transition-colors"
                @click="backClicked()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Back
            </button>
            <button v-if="is_from_mobile == 0" type="button" aria-label="Print invoice"
                class="inline-flex items-center gap-[8px] h-[36px] px-[16px] rounded-full border-1 border-[#8479E1] bg-[#fff] text-[13px] font-medium text-[#8479E1] hover:bg-[#F4F2FD]"
                @click="printPdf()">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M7 9V3h10v6M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2M7 14h10v7H7z"
                        stroke="#8479E1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Print
            </button>
        </div>

        <article class="rounded-[16px] bg-white shadow-[0px_0px_10px_0px_#0000001A] overflow-hidden print:shadow-none print:rounded-none">
            <!-- company band -->
            <div v-if="header"
                class="printExact flex mobile:flex-col tablet:flex-row tablet:items-center justify-between gap-[10px] px-[15px] laptop:px-[25px] py-[15px] bg-[#8479E1] text-white">
                <div class="min-w-0">
                    <p class="m-0 text-[20px] tablet:text-[24px] font-semibold break-words">{{ header.company_name }}</p>
                    <p v-if="header.company_license" class="m-0 text-[13px] opacity-[0.9]">Company License # {{ header.company_license }}</p>
                </div>
                <div class="min-w-0 tablet:text-right text-[13px]">
                    <p v-if="header.company_address" class="m-0 break-words">{{ header.company_address }}</p>
                    <a v-if="header.customer_service_phone" :href="`tel:${header.customer_service_phone.replace(/\D/g, '')}`"
                        class="block no-underline text-white hover:underline">{{ $helper.formatPhoneNumber(header.customer_service_phone) }}</a>
                    <a v-if="header.customer_service_email" :href="`mailto:${header.customer_service_email}`"
                        class="block no-underline text-white hover:underline break-all">{{ header.customer_service_email }}</a>
                </div>
            </div>

            <div class="px-[15px] laptop:px-[25px] py-[20px]">
                <!-- bill to | invoice number -->
                <div v-if="info" class="grid tablet:grid-cols-2 gap-[15px]">
                    <div class="tablet:order-2 tablet:text-right">
                        <p class="m-0 text-[22px] tablet:text-[28px] font-bold text-[#8479E1]">Invoice # {{ info.invoice_id }}</p>
                        <p class="m-0 text-[16px] font-semibold text-[#333333]">Booking # {{ info.job_id }}</p>
                        <p class="m-0 mt-[4px] text-[13px] text-[#666666]">Date of Invoice: {{ invoiceDate }}</p>
                        <p v-if="isPaid" class="m-0 text-[13px] text-[#666666]">Invoice Paid: {{ paidDate }}</p>
                        <span v-if="info.status"
                            class="printExact inline-block mt-[6px] px-[10px] py-[3px] rounded-full text-[12px] font-medium"
                            :class="isPaid ? 'bg-[#E7F7F0] text-[#1FA971]' : 'bg-[#FEF6E6] text-[#C98305]'">{{ info.status }}</span>
                    </div>
                    <div class="tablet:order-1 min-w-0 p-[15px] rounded-[10px] bg-[#F8F7FF]">
                        <p class="m-0 text-[12px] uppercase tracking-[0.5px] text-[#999999]">Invoice To</p>
                        <p class="m-0 mt-[2px] text-[18px] font-semibold text-[#333333] break-words">{{ info.consumer_name }}</p>
                        <p v-if="info.address" class="m-0 mt-[4px] text-[13px] text-[#333333] break-words">
                            <span class="text-[#EA8A8A]">Service Address:</span> {{ info.address }}</p>
                        <a v-if="info.email" :href="`mailto:${info.email}`"
                            class="block mt-[2px] text-[13px] no-underline text-[#7695FF] break-all">{{ info.email }}</a>
                    </div>
                </div>

                <!-- line items -->
                <template v-if="lineItems.length">
                    <div class="printExact hidden tablet:grid grid-cols-[50px_80px_1fr_120px] gap-[10px] mt-[25px] px-[14px] py-[10px] rounded-[8px] bg-[#F3F4F7] text-[13px] font-medium text-[#5B616B]">
                        <span>#</span><span>Quantity</span><span>Description</span><span class="text-right">Total</span>
                    </div>
                    <div class="mobile:mt-[20px] tablet:mt-0">
                        <div v-for="(item, index) in lineItems" :key="index"
                            class="grid grid-cols-[1fr_auto] tablet:grid-cols-[50px_80px_1fr_120px] gap-[10px] px-[14px] py-[11px] border-b-1 border-[#EEF0F4] text-[14px] text-[#333333]">
                            <span class="hidden tablet:block text-[#999999]">{{ index + 1 }}</span>
                            <span class="hidden tablet:block">{{ item.quantity }}</span>
                            <span class="min-w-0 break-words">{{ item.item_name }}<span class="tablet:hidden text-[#999999]"> x {{ item.quantity }}</span></span>
                            <span class="text-right text-[13px] font-medium text-[#1FA971]">Included</span>
                        </div>
                    </div>
                </template>

                <!-- totals -->
                <div class="flex justify-end mt-[20px]">
                    <div class="w-full tablet:w-[380px] flex flex-col text-[14px] text-[#333333]">
                        <div class="flex justify-between gap-[12px] px-[14px] py-[8px] border-b-1 border-[#EEF0F4]">
                            <span>Sales Tax</span><span>{{ taxText }}</span>
                        </div>
                        <div v-if="invoicePdf?.deppClaimDiscount !== null && invoicePdf?.deppClaimDiscount !== undefined"
                            class="flex justify-between gap-[12px] px-[14px] py-[8px] border-b-1 border-[#EEF0F4]">
                            <span>Claim Discount</span><span>${{ $helper.formatAmount(invoicePdf.deppClaimDiscount) }}</span>
                        </div>
                        <div v-if="data.discount_amount" class="flex justify-between gap-[12px] px-[14px] py-[8px] border-b-1 border-[#EEF0F4]">
                            <span>Claim Discount</span><span>${{ $helper.formatAmount(data.discount_amount) }}</span>
                        </div>
                        <div class="printExact flex items-center justify-between gap-[12px] mt-[10px] px-[14px] py-[12px] rounded-[10px] bg-[#F3F4F7]">
                            <span class="text-[15px] font-semibold text-[#2E3138]">Total</span>
                            <span class="text-[20px] font-bold text-[#0E2F7E]">${{ $helper.formatAmount(data.total) }}</span>
                        </div>
                        <div v-if="isPaid && data.payment_type" class="flex justify-between gap-[12px] px-[14px] pt-[10px] text-[13px] text-[#666666]">
                            <span>Payment Type</span><span class="text-right">{{ data.payment_type }}</span>
                        </div>
                    </div>
                </div>

                <!-- customer approval + signature (paid invoices) -->
                <div v-if="data.device_info && isPaid"
                    class="flex mobile:flex-col tablet:flex-row tablet:items-end justify-between gap-[15px] mt-[25px] p-[15px] rounded-[10px] border-1 border-[#E1E5EF]">
                    <div class="grid gap-[4px] text-[13px] text-[#333333]">
                        <p class="m-0"><span class="text-[#EA8A8A]">Approved by the Customer:</span> {{ info.last_name }}, {{ info.first_name }}</p>
                        <p class="m-0"><span class="text-[#EA8A8A]">IP:</span> {{ data.device_info.client_ip }}</p>
                        <p class="m-0"><span class="text-[#EA8A8A]">Date Time:</span> {{ data.device_info.created_date_time }}</p>
                        <p class="m-0"><span class="text-[#EA8A8A]">Device Type:</span> {{ data.device_info.device_type }}</p>
                    </div>
                    <div v-if="invoicePdf?.signature" class="flex flex-col items-start tablet:items-end">
                        <span class="text-[12px] text-[#999999]">Signature</span>
                        <img :src="invoicePdf.signature" alt="Signature" class="w-[200px] h-[110px] object-contain border-b-1 border-[#CCCCCC]">
                    </div>
                </div>

                <p class="m-0 mt-[25px] pt-[15px] border-t-1 border-[#EEF0F4] text-center text-[13px] text-[#999999]">
                    Invoice was created on a computer and is valid without the signature and seal.
                </p>
            </div>
        </article>
    </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";

export default {
    data() {
        return {
            is_from_mobile: (this.$route.query.is_from_mobile ?? 0) == 1
        };
    },

    computed: {
        ...mapGetters(["invoicePdf", "apiCallback"]),
        data() {
            return this.invoicePdf?.data || {};
        },
        header() {
            return this.data.header;
        },
        info() {
            return this.data.info;
        },
        lineItems() {
            return Array.isArray(this.data.line_items) ? this.data.line_items : [];
        },
        isPaid() {
            return this.info?.is_paid != 0;
        },
        // created_at is unix seconds (new Date() on it gave "Invalid Date")
        invoiceDate() {
            return Number(this.info?.created_at) > 0 ? this.$helper.formattedDate(this.info.created_at) : "-";
        },
        // paid_date is a plain date ("2026-11-03"): read it as a local date, not UTC midnight
        paidDate() {
            const d = moment(this.info?.paid_date, "YYYY-MM-DD", true);
            return d.isValid() ? d.format("MM/DD/YYYY") : (this.info?.paid_date || "-");
        },
        taxText() {
            const tax = this.data.line_items?.tax_amount;
            return tax != null ? `$${this.$helper.formatAmount(tax)}` : "0.00";
        },
    },

    mounted() {
        this.getInvoicePdf({
            invoice_id: this.$route.params.id
        })
    },

    methods: {
        ...mapActions(["getInvoicePdf"]),

        printPdf() {
            window.print()
        },

        backClicked() {
            this.$router.go(-1);
        },
    }
}
</script>
<style scoped>
/* keep the band / header row / status colours when printing */
@media print {
    .printExact {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
    }
}
</style>
